/**
 * @version 2.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://gitbook.kelnik.ru/main/front-end/components/popup.html documentation
 */

/**
 * DEPENDENCIES
 */
import {InternetConnection, Utils} from '../../common/scripts/utils';
import basicTemplate from './templates/basic-popup.twig';
import contentTemplate from './templates/content-popup.twig';
import fullTemplate from './templates/full-popup.twig';
import galleryTemplate from './templates/gallery-popup.twig';
import Hammer from 'hammerjs';
import imageTemplate from './templates/image-popup.twig';
import {initSwiperSlider} from '../../common/scripts/common';
import Observer from '../../common/scripts/observer';
import onlineTemplate from './templates/online-popup.twig';
import progressContentTemplate from './templates/progress-content.twig';
import progressTemplate from './templates/progress-popup.twig';
import TouchZoom from '../touch-zoom/touch-zoom';

const observer = new Observer();
const emptyFunction = () => {
    return null;
};

const pageWrapper = document.querySelector('.j-wrapper');

export class AbstractPopup {
    constructor(options = {}) {
        this.openStateClass = options.openStateClass || 'popup_state_open';
        this.closeButtonAriaLabel = options.closeButtonAriaLabel || 'Закрыть всплывающее окно';
        this.openBtns = options.openButtons;
        this.currentState = false;
        this.onCreate = options.onCreate || emptyFunction;
        this.onClose = options.onClose || emptyFunction;

        /* Определяется в методе _getElements() */
        this.popup = null;
        this.content = null;
        this.closeBtns = null;

        /* Определяется в методе _setTemplates() */
        this.template = null;

        /* Определяется в методе _setData() */
        this.data = null;

        this._bindMethodsContext();
    }

    /**
     * @Public
     * Входная точка проекта
     *
     */
    init() {
        this._bindOpenEvents();
    }

    /**
     * @Public
     * Отображение попапа на экране
     *
     */
    showPopup() {
        this.popup.classList.add(this.openStateClass);

        Utils.bodyFixed(this.popup);

        // Ставим доп класс для коректной работы мобильного меню
        if (Utils.isMobile()) {
            pageWrapper?.classList.add('is-popup-open');
        }
    }

    /**
     * @Public
     * Открытие попапа
     *
     */
    displayPopup() {
        this.showPopup();

        observer.publish('popup:open', this);
        observer.publish('swiper-carousel:refresh', this.popup);
        observer.publish('tabs:refresh', this.popup);
        observer.publish('initDeveloperRoadmapMap');
        this.currentState = true;
    }

    /**
     * @Public
     * Сокрытие попапа с дисплея
     *
     */
    hidePopup() {
        this.popup.classList.remove(this.openStateClass);

        Utils.bodyStatic();
    }

    /**
     * @Public
     * Удаление попапа из DOM-дерева
     *
     */
    removePopup() {
        this.hidePopup();
        Utils.removeElement(this.popup);

        observer.publish('popup:close', this);

        this.currentState = false;

        document.removeEventListener('keyup', this._closeByKeyboard);
        document.removeEventListener('click', this._closeByMouse);

        this.onClose();

        // Удаляем доп класс для коректной работы мобильного меню если он был
        pageWrapper?.classList.remove('is-popup-open');
    }

    /**
     * @Public
     * Перезапуск попапа
     *
     */
    reOpen() {
        this.removePopup();
        this._open();
    }

    /**
     * @Private
     * Бинд контекста для методов класса
     *
     */
    _bindMethodsContext() {
        this._closeByKeyboard = this._closeByKeyboard.bind(this);
        this.removePopup = this.removePopup.bind(this);
    }

    /**
     * @Private
     * Установка шаблона попапа
     *
     */
    _setTemplates() {
        this.template = basicTemplate;
    }

    /**
     * @Abstract Private
     * Установка данных в попапе
     *
     */
    _setData() {
        throw new Error(`Method _setData() must be overridden`);
    }

    /**
     * @Private
     * Вставка ноды попапа в DOM-дерево
     *
     */
    _setPopupOnPage() {
        document.body.insertAdjacentHTML('afterbegin', this.template(this.data));
    }

    /**
     * @Private
     * Получение нод DOM-дерева из попапа
     *
     */
    _getElements() {
        this.popup = document.querySelector('.j-popup__body');
        this.content = document.querySelector('.j-popup__content');

        const closeBtn = this.popup.querySelectorAll('.j-popup__close');
        const overLay = this.popup.querySelector('.j-popup__overlay');

        this.closeBtns = [...closeBtn, overLay];
    }

    /**
     * @Private
     * Генерация попапа
     *
     */
    _createPopup() {
        this._setData();
        this._setTemplates();
        this._setPopupOnPage();
        this._getElements();
        this._bindCloseEvents();

        this.onCreate();
    }

    /**
     * @Abstract Private
     * Коллбэк для события открытия
     *
     */
    _open() {
        throw new Error(`Method _open() must be overridden`);
    }

    /**
     * @Abstract Private
     * Коллбэк для события по клику
     *
     */
    _onClick() {
        throw new Error(`Method _onClick() must be overridden`);
    }

    /**
     * @Private
     * Привязка методов к событиям открытия попапа
     *
     */
    _bindOpenEvents() {
        this.openBtns.forEach((button, index) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                if (this.currentState) {
                    return;
                }

                this.currentTarget = button;

                this._open();
                this._onClick(index);
            });
        });
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа
     *
     */
    _bindCloseEvents() {
        this._closeByMouse();
        document.addEventListener('keyup', this._closeByKeyboard);
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа ко клику мышью
     *
     */
    _closeByMouse() {
        this.closeBtns.forEach((closeBtn) => {
            closeBtn.addEventListener('click', this.removePopup);
        });
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа по вводу с клавиатуры
     * @param {number} key - числовой код нажатой клавиши
     */
    _closeByKeyboard(key) {
        const keyCodeESC = 27;

        if (key.keyCode === keyCodeESC) {
            this.removePopup();
        }
    }

    /**
     * @Private
     * Получение необходимого дата-атрибута с кнопок открытия попапа
     * @param {string} attribute - атрибут, по которому происходит поиск
     * @return {String} - значение дата-атрибута
     *
     */
    _getDataAttrValue(attribute) {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute(attribute);
        }))];

        if (attributesArray.length !== 1 || !attributesArray[0]) {
            throw new Error(`Expect one ${attribute}. You set: ${attributesArray[0] ? attributesArray : 0}`);
        }

        return attributesArray[0];
    }
}

export class StaticPopup extends AbstractPopup {
    constructor(options = {}) {
        super(options);

        /* @Override */
        this.closeStateClass = options.closeStateClass || 'popup_state_close';
        this.closeButtonAriaLabel = 'Закрыть';
        this.modify = options.modify || '';
        this.removeDelay = options.removeDelay || 0;
    }


    /* @Override */
    _setData() {
        const contentLink = this._getDataAttrValue('data-href');

        this.data = {
            content        : document.getElementById(contentLink).innerHTML,
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify ? this.modify : this._getDataAttrValue('data-modify')
        };
    }

    /* @Override */
    _open() {
        this._createPopup();
        this.displayPopup();
    }

    /* @Override */
    // eslint-disable-next-line
    _onClick() {}

    /* @Override */
    removePopup() {
        if (this.removeDelay) {
            this.popup.classList.remove(this.openStateClass);
            this.popup.classList.add(this.closeStateClass);

            setTimeout(() => {
                super.removePopup();
            }, this.removeDelay);
        } else {
            super.removePopup();
        }
    }
}

export class StaticPopupWithHash extends StaticPopup {
    _getElements() {
        super._getElements();
        this.popupHeader = document.querySelector('.j-popup__header');
    }

    /* @Extend */
    _bindOpenEvents() {
        super._bindOpenEvents();

        this._parseUrl();
    }

    _bindCloseEvents() {
        super._bindCloseEvents();

        this._closeBySwipe();
    }

    _closeBySwipe() {
        if (this.popupHeader) {
            const touchEvent = new Hammer(this.popupHeader);

            touchEvent.get('swipe').set({direction: Hammer.DIRECTION_ALL});

            touchEvent.on('swipedown', this.removePopup);
        }
    }

    /* @Extend */
    displayPopup() {
        super.displayPopup();

        this._setHash();
        window.isSPWHOpen = true;
    }

    /* @Extend */
    removePopup() {
        super.removePopup();

        this._clearHash();
        window.isSPWHOpen = false;
    }

    /**
     * @Private
     * Открывает попап по хэшу в омнибоксе
     */
    _parseUrl() {
        const contentLink = this._getDataAttrValue('data-href');

        if (this.currentState || window.location.hash !== `#${contentLink}` || window.isSPWHOpen) {
            return;
        }

        this._createPopup();
        this.displayPopup();
    }

    /**
     * @Private
     * Устанавливает хэш попапа в омнибокс браузера
     */
    _setHash() {
        const contentLink = this._getDataAttrValue('data-href');

        window.history.pushState(null, null, `#${contentLink}`);
    }

    /**
     * @Private
     * Очищает омнибокс браузера от хэша попапа
     */
    _clearHash() {
        if (window.history.pushState) {
            window.history.pushState(null, null, window.location.pathname + window.location.search);

            return;
        }

        window.location.hash = '';
    }
}

export class StaticPopupWithImage extends StaticPopup {
    _setTemplates() {
        this.template = imageTemplate;
    }

    /* @Override */
    _setData() {
        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify,
            src            : this.openBtns[0].getAttribute('data-image'),
            caption        : this.openBtns[0].getAttribute('data-caption'),
            alt            : this.openBtns[0].getAttribute('data-alt')
        };
    }

    _onClick() {
        this._initPinch();
    }

    _initPinch() {
        if (!Utils.isTouchDevice()) {
            return;
        }

        const images = Array.from(this.popup.querySelectorAll('img'));

        images.forEach((item) => {
            new TouchZoom(item); // eslint-disable-line
        });
    }
}

export class VideoPopup extends StaticPopup {
    // /* @Override */
    _setTemplates() {
        this.template = contentTemplate;
    }

    /* @Override */
    _setData() {
        const dataAttributeVideo = this.currentTarget.dataset.video;

        if (!dataAttributeVideo.length) {
            return;
        }

        const video = {
            video: dataAttributeVideo
        };

        this.data = {
            content        : video,
            modify         : this.modify ? this.modify : this._getDataAttrValue('data-modify'),
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }
}

export class ZoomPopup extends StaticPopup {
    // /* @Override */
    _setTemplates() {
        this.template = contentTemplate;
    }

    /* @Override */
    _setData() {
        const dataSrc = this.currentTarget.dataset.zoom;
        const dataAlt = this.currentTarget.dataset.alt;

        if (!dataSrc.length) {
            return;
        }

        const zoom = {
            src: dataSrc,
            alt: dataAlt || 'Добавьте описание изображению'
        };

        this.data = {
            content        : {zoom},
            modify         : this.modify ? this.modify : this._getDataAttrValue('data-modify'),
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }
}

export class AjaxPopup extends AbstractPopup {
    constructor(options) {
        super(options);

        /* Определяется в методе _sendDataRequest() */
        this.contentInfo = null;

        /* Определяется в методе _resending() */
        this.setInterval = null;

        this._setData = this._setData.bind(this);
        this.serverConnection = new InternetConnection(this._sendDataRequest.bind(this));
    }

    /**
     * @Private
     * Проверка на наличие интернета.
     * @return {String} Метод запроса из атрибута или POST, если атрибут пуст.
     */
    _getQueryType() {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute('data-query');
        }))];

        if (attributesArray.length > 1) {
            throw new Error(`Expect maximum one data-query. You set: ${attributesArray}`);
        }

        return attributesArray[0] || 'POST';
    }

    /**
     * @Private
     * Отправляет запрос на сервер и вызывает создание попапа с данными из ответа сервера
     */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-ajax');
        const callback = {
            success(req) {
                that.contentInfo = req.data.content;
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.contentInfo = that.contentInfo || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();

                that.contentInfo = null;
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _open() {
        this.serverConnection.connect();
    }

    /* @Override */
    _setData() {
        this.data = {
            contentInfo    : this.contentInfo,
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }

    /* @Override */
    // eslint-disable-next-line no-empty-function
    _onClick() {}
}

export class JsonPopup extends StaticPopup {
    // /* @Override */
    _setTemplates() {
        this.template = contentTemplate;
    }

    /* @Override */
    _setData() {
        const dataAttributeJson = this.currentTarget.dataset.json;

        if (!dataAttributeJson.length) {
            return;
        }

        const dataJson = JSON.parse(Utils.fromBase64(dataAttributeJson));

        this.data = {
            content        : dataJson.data.content,
            modify         : this.modify ? this.modify : this._getDataAttrValue('data-modify') || '',
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }
}

export class FullPopup extends StaticPopup {
    constructor(options) {
        super(options);

        this.name = options.name;
    }

    /* @Override */
    _setTemplates() {
        this.template = fullTemplate;
    }

    /* @Override */
    _onClick() {
        super._onClick();
        this._initGallery();
    }

    /* @Override */
    _initGallery() {
        const gallery = this.content.querySelector('.j-swiper-slider');

        if (gallery) {
            initSwiperSlider({wrapper: gallery});
        }
    }

    /* @Override */
    _setData() {
        const contentLink = this._getDataAttrValue('data-full');
        const modifyClass = this._getDataAttrValue('data-modify');

        this.data = {
            content        : document.getElementById(contentLink).innerHTML,
            modify         : modifyClass ? modifyClass : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            coords         : this.currentTarget.dataset.coords
        };

        observer.publish('lazyLoad:refresh', document.getElementById(contentLink));
    }
}

export class GalleryPopup extends StaticPopup {
    constructor(options) {
        super(options);

        /* @Override */
        this.closeButtonAriaLabel = 'Закрыть';
        this.gallerySelector = '.j-popup-gallery';
    }

    /* @Override */
    _setTemplates() {
        this.template = galleryTemplate;
    }

    /* @Override */
    _setData() {
        this.data = {
            modify         : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            items          : this._collectSlidesData()
        };
    }

    _collectSlidesData() {
        return this.openBtns.reduce((resultArray, item) => {
            if (!item.closest('.cloned-slide')) {
                resultArray.push({
                    src    : item.getAttribute('data-src'),
                    alt    : item.getAttribute('data-alt'),
                    caption: item.getAttribute('data-caption')
                });
            }

            return resultArray;
        }, []);
    }

    /* @Override */
    _onClick() {
        this.gallery = this.popup.querySelector(this.gallerySelector);
        this._initPinch();
    }

    _initPinch() {
        const images = Array.from(this.gallery.querySelectorAll('img'));

        images.forEach((item) => {
            // eslint-disable-next-line no-new
            new TouchZoom(item);
        });
    }
}

export class ProgressPopup extends AjaxPopup {
    /* @Override */
    constructor(options) {
        super(options);

        this.modify = options.modify || '';
        this.currentItemId = null;
        this.currentItemIndex = null;
        this.templateContent = progressContentTemplate;
        this.navButtons = null;
    }

    /* @Override */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-ajax');
        const callback = {
            success(req) {
                that.contentInfo = req.data;
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.contentInfo = that.contentInfo || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _setTemplates() {
        this.template = progressTemplate;
    }

    /* @Override */
    _setData() {
        this.currentItemIndex = this.contentInfo.albums.findIndex((item) => {
            return Number(item.id) === Number(this.currentItemId);
        });
        this.data = {
            modify         : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            progress       : this.contentInfo.albums[this.currentItemIndex],
            prev           : this.contentInfo.albums[this.currentItemIndex - 1] || false,
            next           : this.contentInfo.albums[this.currentItemIndex + 1] || false
        };
    }

    /* @Override */
    _onClick() {
        this.currentItemId = this.currentTarget.dataset.id;
    }

    _createPopup() {
        super._createPopup();
        this._initChange();
    }

    _initChange() {
        this.navButtons = Array.from(this.content.querySelectorAll('.j-progress-nav'));

        if (this.navButtons.length) {
            this.navButtons.forEach((navButton) => {
                navButton.addEventListener('click', this._changeContent.bind(this));
            });
        }
    }

    _changeContent(event) {
        this.currentItemId = event.currentTarget.dataset.id;
        this._setData();
        Utils.clearHtml(this.content);
        Utils.insetContent(this.content, this.templateContent(this.data));
        this.popup.scroll({top     : 0,
            behavior: 'smooth'});
        this._initChange();
    }
}

export class OnlinePopup extends AjaxPopup {
    /* @Override */
    constructor(options) {
        super(options);

        this.modify = options.modify || '';
    }

    /* @Override */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-ajax');
        const callback = {
            success(req) {
                that.contentInfo = req.data;
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.contentInfo = that.contentInfo || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _setTemplates() {
        this.template = onlineTemplate;
    }

    /* @Override */
    _setData() {
        this.data = {
            modify         : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            tabs           : this.contentInfo.cameras
        };
    }
}

export class PostMessagePopup extends StaticPopup {
    /**
     * @override
     */
    _setData() {
        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.premissesData?.modify ? this.premissesData?.modify : this.modify,
            content        : document.getElementById(this.premissesData.href)?.innerHTML
        };
    }

    /**
     * @override
     */
    _open() {
        this._createPopup();
        this._appendData();
        this.displayPopup();
    }

    /**
     * @override
     */
    _bindOpenEvents() {
        window.addEventListener('message', (event) => {
            if (event.origin !== window.origin) {
                return;
            }

            if (event.data?.message === 'openPopup' && event.data?.href) {
                this.premissesData = event.data;

                if (this.currentState) {
                    return;
                }

                this._open();
            }
        });
    }

    /**
     * Установка шаблона post message попапа
     * @protected
     */
    _setTemplates() {
        this.template = basicTemplate;
    }

    /**
     * Вставляет данные для скрытых полей
     * @private
     */
    _appendData() {
        if (!this.popup) {
            return;
        }

        if (Object.keys(this.premissesData.popupData).length) {
            const popupData = this.premissesData.popupData;
            const details = this.popup.querySelectorAll('.popup__premisses-details-item');
            const form = this.popup.querySelector('form');

            Object.keys(popupData).forEach((key) => {
                details.forEach((node) => {
                    if (node.classList.contains(`popup__premisses-details-${key}`) && popupData[key]) {
                        node.innerHTML = popupData[key] + node.innerHTML;
                    }
                });

                const input = this.popup.querySelector(`input[class="${key}"]`);

                if (input) {
                    input.value = popupData[key];
                } else {
                    const hiddenInput = document.createElement('input');

                    hiddenInput.type = 'hidden';
                    hiddenInput.name = key;
                    hiddenInput.classList.add(key);
                    hiddenInput.value = popupData[key];

                    form.appendChild(hiddenInput);
                }
            });
        }
    }
}
